import { z } from 'zod';
import { jsonObject, jsonValue, riskLevel, sideEffect } from './primitives';
import { normalizedError } from './errors';

/**
 * ToolDefinition (§9.2). The canonical, provider-agnostic description of a tool.
 *
 * The spec types `inputSchema` as a live Zod schema on the server side. That is
 * not serializable, so the **wire** contract carries `inputJsonSchema` (a JSON
 * Schema object). The gateway's in-memory registry holds the live Zod schema and
 * projects it to JSON Schema when a definition crosses a boundary (to the adapter
 * or the web UI). Field kept intentionally opaque here.
 */
export const toolDefinition = z.object({
  name: z.string().min(1), // e.g. "email.search"
  description: z.string(),
  inputJsonSchema: jsonObject,
  sideEffect,
  defaultRisk: riskLevel,
  connectionProvider: z.string().optional(), // e.g. "google"
});
export type ToolDefinition = z.infer<typeof toolDefinition>;

/** A model's intent to call a tool (§8.2 ToolCallIntent). */
export const toolCallIntent = z.object({
  tool: z.string().min(1),
  arguments: jsonObject,
});
export type ToolCallIntent = z.infer<typeof toolCallIntent>;

/**
 * ToolResult (§8.2): discriminated `{ ok:true, data } | { ok:false, error }`.
 * This is the shape a connector returns and the gateway relays to the model
 * (after filtering/truncation, §9.6).
 */
export const toolResult = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), data: jsonValue }),
  z.object({ ok: z.literal(false), error: normalizedError }),
]);
export type ToolResult = z.infer<typeof toolResult>;
